import React from 'react';
import { makeStyles, Tooltip } from '@material-ui/core';
import { designTokens } from '../../../styles/designTokens';
import { parseSchedule } from '../utils/formatters';

const useStyles = makeStyles({
    container: {
        display: 'grid',
        gridRowGap: designTokens.spacing.xs,
        width: '230px',
    },
    iconRow: {
        display: 'grid',
        gridTemplateColumns: '36px repeat(3, 1fr)',
        gridColumnGap: designTokens.spacing.md,
        alignItems: 'center',
        marginBottom: designTokens.spacing.xs,
        '@media (max-width: 900px)': {
            gridTemplateColumns: '36px repeat(3, 1fr)',
            gridColumnGap: designTokens.spacing.sm,
        }
    },
    iconCell: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    },
    icon: {
        width: '20px',
        height: '20px',
        '& path': {
            stroke: designTokens.colors.grey[400],
        }
    },
    tooltip: {
        backgroundColor: designTokens.colors.primary,
        fontSize: '12px',
    },
    arrow: {
        color: designTokens.colors.primary,
    },
    row: {
        display: 'grid',
        gridTemplateColumns: '36px repeat(3, 1fr)',
        gridColumnGap: designTokens.spacing.md,
        alignItems: 'center',
        '@media (max-width: 900px)': {
            gridTemplateColumns: '36px repeat(3, 1fr)',
            gridColumnGap: designTokens.spacing.sm,
        }
    },
    dayLabel: {
        ...designTokens.typography.label,
        fontSize: '12px',
        color: designTokens.colors.text.secondary,
    },
    time: {
        ...designTokens.typography.label,
        fontSize: '12px',
        fontWeight: 500,
        color: designTokens.colors.blue.main,
        textAlign: 'center',
    },
    timeOffday: {
        ...designTokens.typography.label,
        fontSize: '12px',
        fontWeight: 500,
        color: designTokens.colors.blue.main,
        textAlign: 'left',
        paddingLeft: 9,
    }
});

/**
 * Компонент відображення розкладу роботи
 * @param {Object} props
 * @param {string} props.hWork - Рядок годин роботи
 * @param {string} props.zWork - Рядок обідньої перерви
 */
const CollectionHours = ({ hWork, zWork }) => {
    const classes = useStyles();
    const schedule = parseSchedule(hWork, zWork);

    const isSaturdayClosed = typeof schedule.saturday === 'string';
    const isSundayClosed = typeof schedule.sunday === 'string';

    return (
        <div className={classes.container}>
            <div className={classes.iconRow}>
                <div></div>
                <Tooltip
                    title="Відчиняється"
                    arrow
                    placement='top-end'
                    classes={{ tooltip: classes.tooltip, arrow: classes.arrow }}
                >
                    <div className={classes.iconCell}>
                        <svg className={classes.icon} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M10 12.5V14.375M10 12.5C10.8629 12.5 11.5625 11.8004 11.5625 10.9375C11.5625 10.0746 10.8629 9.375 10 9.375C9.13705 9.375 8.4375 10.0746 8.4375 10.9375C8.4375 11.8004 9.13705 12.5 10 12.5ZM7.1875 6.875V4.0625C7.1875 3.31658 7.48382 2.60121 8.01126 2.07376C8.53871 1.54632 9.25408 1.25 10 1.25C10.7459 1.25 11.4613 1.54632 11.9887 2.07376C12.5162 2.60121 12.8125 3.31658 12.8125 4.0625M3.75 6.875H16.25C16.5952 6.875 16.875 7.15482 16.875 7.5V16.25C16.875 16.5952 16.5952 16.875 16.25 16.875H3.75C3.40482 16.875 3.125 16.5952 3.125 16.25V7.5C3.125 7.15482 3.40482 6.875 3.75 6.875Z" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </div>
                </Tooltip>
                <Tooltip
                    title="Відбір біоматеріалу"
                    arrow
                    placement='top-end'
                    classes={{ tooltip: classes.tooltip, arrow: classes.arrow }}
                >
                    <div className={classes.iconCell}>
                        <svg className={classes.icon} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M10.6344 14.9387C11.3988 14.8088 12.1041 14.4447 12.6527 13.8967C13.2014 13.3488 13.5663 12.644 13.6972 11.8797M16.25 11.25C16.25 5.625 10 1.25 10 1.25C10 1.25 3.75 5.625 3.75 11.25C3.75 12.9076 4.40848 14.4973 5.58058 15.6694C6.75268 16.8415 8.3424 17.5 10 17.5C11.6576 17.5 13.2473 16.8415 14.4194 15.6694C15.5915 14.4973 16.25 12.9076 16.25 11.25Z" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </div>
                </Tooltip>
                <Tooltip
                    title="Зачиняється"
                    arrow
                    placement='top-end'
                    classes={{ tooltip: classes.tooltip, arrow: classes.arrow }}
                >
                    <div className={classes.iconCell}>
                        <svg className={classes.icon} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M10 12.5V14.375M10 12.5C10.8629 12.5 11.5625 11.8004 11.5625 10.9375C11.5625 10.0746 10.8629 9.375 10 9.375C9.13705 9.375 8.4375 10.0746 8.4375 10.9375C8.4375 11.8004 9.13705 12.5 10 12.5ZM7.1875 6.875V4.0625C7.1875 3.31658 7.48382 2.60121 8.01126 2.07376C8.53871 1.54632 9.25408 1.25 10 1.25C10.7459 1.25 11.4613 1.54632 11.9887 2.07376C12.5162 2.60121 12.8125 3.31658 12.8125 4.0625V6.875M3.75 6.875H16.25C16.5952 6.875 16.875 7.15482 16.875 7.5V16.25C16.875 16.5952 16.5952 16.875 16.25 16.875H3.75C3.40482 16.875 3.125 16.5952 3.125 16.25V7.5C3.125 7.15482 3.40482 6.875 3.75 6.875Z" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </div>
                </Tooltip>
            </div>

            <div className={classes.row}>
                <div className={classes.dayLabel}>Пн-Пт</div>
                <div className={classes.time}>{schedule.weekdays.start}</div>
                <div className={classes.time}>{schedule.weekdays.lunch}</div>
                <div className={classes.time}>{schedule.weekdays.end}</div>
            </div>

            <div className={classes.row}>
                <div className={classes.dayLabel}>Сб</div>
                {isSaturdayClosed ? (
                    <div className={classes.timeOffday} style={{ gridColumn: '2 / -1' }}>
                        {schedule.saturday}
                    </div>
                ) : (
                    <>
                        <div className={classes.time}>{schedule.saturday.start}</div>
                        <div className={classes.time}>{schedule.saturday.lunch}</div>
                        <div className={classes.time}>{schedule.saturday.end}</div>
                    </>
                )}
            </div>

            <div className={classes.row}>
                <div className={classes.dayLabel}>Нд</div>
                {isSundayClosed ? (
                    <div className={classes.timeOffday} style={{ gridColumn: '2 / -1' }}>
                        {schedule.sunday}
                    </div>
                ) : (
                    <>
                        <div className={classes.time}>{schedule.sunday.start}</div>
                        <div className={classes.time}>{schedule.sunday.lunch}</div>
                        <div className={classes.time}>{schedule.sunday.end}</div>
                    </>
                )}
            </div>
        </div>
    );
};

export default CollectionHours;
